import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useCallback } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { withRouter } from '@/checkout-instore-legacy/utils/withRouter'
import Button from '@vtex/styleguide/lib/Button'
import flatten from 'lodash/flatten'
import isEmpty from 'lodash/isEmpty'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import BackHeader from '@/checkout-instore-legacy/components/Commons/BackHeader'
import Loader from '@/checkout-instore-legacy/components/Commons/Loader/index'
import {
  createGiftCardConfirmationModalContext,
  createCancelModalContext,
} from '@/checkout-instore-legacy/returnExchange/utils'
import BarcodeReader from '@/checkout-instore-legacy/utils/BarcodeReader'
import { FEATURES } from '../constants'

import ItemCardList from './ItemCardList'
import OrderSummaryCard from './OrderSummaryCard'
import ProductBarcodeArea from './ProductBarcodeArea'

const ExchangeSummary = ({
  itemMap,
  order,
  profile,
  errors,
  giftcardLoading,
  generateGiftcard,
  setGiftcardLoading,
  itemLoading,
  setItemLoading,
  clearReturnExchange,
  clearOrder,
  clearItemMap,
  clearError,
  clearAllErrors,
  searchEan,
  changeItemQuantity,
  removeItem,
  openRootModalWithDialog,
  router,
  intl,
}) => {
  useEffect(() => {
    const handleBarcodeRead = (event) => {
      let { barcode } = event.data
      if (barcode) {
        barcode = String(barcode)
      }
      searchEan(barcode)
    }

    BarcodeReader.listen(handleBarcodeRead)
    window.handleBarcodeRead = (barcode) =>
      handleBarcodeRead({ data: { barcode } })
    return () => {
      BarcodeReader.unlisten(handleBarcodeRead)
    }
  }, [searchEan])

  useEffect(() => {
    setGiftcardLoading(false)
    setItemLoading(false)
  }, [setGiftcardLoading, setItemLoading])
  const items = useMemo(
    () => Object.keys(itemMap).map((ean) => ({ ...itemMap[ean], ean })),
    [itemMap]
  )
  const orderItems = useMemo(() => {
    if (order) {
      return flatten(
        items.map((item) => {
          const orderItem = order.items.find(
            (orderItem) => orderItem.id === String(item.id)
          )
          return orderItem
            ? [{ ...orderItem, ean: item.ean, quantity: item.quantity }]
            : []
        })
      )
    }
    return []
  }, [items, order])

  const hasItemOrderInconsistency = useMemo(
    () => items.some((item) => item.orderItemInconsistency),
    [items]
  )

  const orderInconsistencyError = errors[FEATURES.ORDER_PRODUCTS]
  const itemError = errors[FEATURES.PRODUCTS]

  const handleClearError = useCallback(
    () => clearError(FEATURES.ORDER_PRODUCTS),
    [clearError]
  )

  useEffect(() => {
    if (!hasItemOrderInconsistency && orderInconsistencyError) {
      handleClearError()
    }
  })

  const handleSubmitOnClick = useCallback(() => {
    const modalContext = createGiftCardConfirmationModalContext(() => {
      generateGiftcard(profile)
    }, intl)
    openRootModalWithDialog(modalContext)
  }, [generateGiftcard, intl, openRootModalWithDialog, profile])

  const handleGoToClientPage = useCallback(() => {
    router.push(`return-exchange/${order.orderId}/client`)
  }, [order.orderId, router])

  const handleCancel = useCallback(() => {
    const modalContext = createCancelModalContext(() => {
      router.push('/')
      clearReturnExchange()
    }, intl)
    openRootModalWithDialog(modalContext)
  }, [clearReturnExchange, intl, openRootModalWithDialog, router])

  const handleGoBack = useCallback(() => {
    clearItemMap()
    clearOrder()
    clearAllErrors()
    router.push('/return-exchange')
  }, [clearAllErrors, clearItemMap, clearOrder, router])

  if (!order) {
    return null
  }

  return (
    <React.Fragment>
      <Loader loading={giftcardLoading} />
      <div className="flex flex-auto flex-column items-center bg-muted-5 c-on-base pt8 pb5 overflow-auto">
        <div className="flex flex-column w-50">
          <BackHeader
            messageId="returnExchange.summary.back"
            onClick={handleGoBack}
          />
          <div className="flex flex-row justify-between items-center">
            <span className="t-heading-2 pt2">
              <FormattedMessage id="returnExchange.summary.title" />
            </span>
            <span
              className="c-action-primary t-action pointer"
              onClick={handleGoBack}
            >
              <FormattedMessage id="returnExchange.summary.edit" />
            </span>
          </div>
          <div className="pt7">
            <OrderSummaryCard order={order} items={orderItems} />
          </div>
          <div className="pt4">
            <ItemCardList
              clearError={handleClearError}
              error={orderInconsistencyError}
              items={items}
              handleRemoveItem={removeItem}
              showPrice
              handleChangeItemQuantity={changeItemQuantity}
            />
          </div>
          <div>
            <ProductBarcodeArea
              itemError={itemError}
              searchEan={searchEan}
              itemLoading={itemLoading}
            />
          </div>
          <div className="self-end pt7">
            <Button variation="tertiary" onClick={handleCancel}>
              <FormattedMessage id="returnExchange.summary.button.cancel" />
            </Button>
            {profile ? (
              <Button
                variation="primary"
                disabled={hasItemOrderInconsistency || isEmpty(itemMap)}
                onClick={handleSubmitOnClick}
              >
                <FormattedMessage id="returnExchange.summary.button.giftcard" />
              </Button>
            ) : (
              <Button
                variation="primary"
                disabled={hasItemOrderInconsistency || isEmpty(itemMap)}
                onClick={handleGoToClientPage}
              >
                <FormattedMessage id="returnExchange.summary.button.client" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

ExchangeSummary.propTypes = {
  intl: intlShape.isRequired,
  router: PropTypes.any,
  order: PropTypes.object,
  profile: PropTypes.object,
  itemMap: PropTypes.object,
  errors: PropTypes.object,
  clearReturnExchange: PropTypes.func.isRequired,
  clearOrder: PropTypes.func.isRequired,
  giftcardLoading: PropTypes.bool,
  itemLoading: PropTypes.bool,
  generateGiftcard: PropTypes.func.isRequired,
  setGiftcardLoading: PropTypes.func.isRequired,
  setItemLoading: PropTypes.func.isRequired,
  searchEan: PropTypes.func.isRequired,
  changeItemQuantity: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  clearItemMap: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  openRootModalWithDialog: PropTypes.func.isRequired,
  clearAllErrors: PropTypes.func.isRequired,
}

export default withRouter(injectIntl(ExchangeSummary))
